/**
 * PaymentContext — country/language routing based on the URL path and
 * IP-based geolocation (ipinfo.io). Legacy CRM API fetchers (tenancy,
 * inspectionpackagepairs, leads OTP, utility) were removed 2026-07-30 —
 * they targeted endpoints that no longer exist at crm.wisedrive.in.
 *
 * The modern paid-booking flow lives in `services/crmApi.js` (packages,
 * orders, OTP, vehicles/lookup) and is consumed directly by
 * InspectionPackages.jsx and Services.jsx. This context only holds the
 * "which country is the user in" signal now.
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useCurrency } from './CurrencyContext';

const PaymentContext = createContext();

export const PaymentProvider = ({ children }) => {
    const { changeCountry, changeLanguage } = useCurrency();

    const [country, setCountry] = useState(null);
    const path = window.location.href.split('/')[3];

    const countries = [
        { name: 'India', value: 'in' },
        { name: 'Indonesia', value: 'id' },
        { name: 'Malaysia', value: 'my' },
        { name: 'Thailand', value: 'th' },
    ];

    useEffect(() => {
        const fetchCountryData = async () => {
            try {
                const response = await fetch('https://ipinfo.io/json');
                const data = await response.json();
                handleCountryData(data);
            } catch (error) {
                // ipinfo can fail (rate limit, ad-block); fall back to /IN.
                handleCountryData({ country: 'IN' });
            }
        };

        const handleCountryData = (data) => {
            const fetchedCountryCode = data?.country;
            if (!path || path === '') {
                if (fetchedCountryCode === 'MY') {
                    window.location.replace('/my');
                } else if (fetchedCountryCode === 'IN') {
                    window.location.replace('/in');
                } else {
                    window.location.replace('/in');
                }
                return;
            }

            const countryPath = countries.find(
                (c) => c.value === path,
            );
            handlePathBasedCountry(path, countryPath);
        };

        const handlePathBasedCountry = (currentPath, countryPath) => {
            const normalized = String(currentPath || '').toLowerCase();
            switch (normalized) {
                case 'in':
                    setCountry('in');
                    changeCountry('IN');
                    changeLanguage('en');
                    break;
                case 'id':
                    setCountry('id');
                    break;
                case 'my':
                    setCountry('my');
                    changeCountry('MY');
                    changeLanguage('bm');
                    break;
                case 'th':
                    setCountry('th');
                    break;
                case 'googleadsleadgen':
                    setCountry('googleadsleadgen');
                    break;
                default:
                    setCountry('cs');
                    break;
            }
        };

        fetchCountryData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [path]);

    return (
        <PaymentContext.Provider value={{ country }}>
            {children}
        </PaymentContext.Provider>
    );
};

export const usePayment = () => {
    const context = useContext(PaymentContext);
    if (!context) {
        throw new Error('usePayment must be used within a PaymentProvider');
    }
    return context;
};

export default PaymentContext;
